import { z } from 'zod';
import { AncillaryDirectionSchema, type AncillaryDirection } from './views.js';

/**
 * Evento do legado sobre um voucher FILHO de ancilar (TRAVEL_SEAT/TRAVEL_LUGGAGE),
 * normalizado pelo voucher-events e entregue ao modal-session em
 * `POST /internal/ancillary-events`. O vínculo com o pedido do voll-intelligence
 * NÃO depende de campo nosso no legado: sai do voucher PAI que o próprio evento
 * carrega (`value.seat.voucherId` / `value.luggage.voucherId`), casado com a sessão
 * no modal-session. Spec §6.5.
 */

export const AncillaryLegacyTopicSchema = z.enum([
  'voucher.created',
  'voucher.finished',
  'voucher.quotation.requested',
  'voucher.issue.requested',
  'voucher.ancillary.handoff.requested',
]);
export type AncillaryLegacyTopic = z.infer<typeof AncillaryLegacyTopicSchema>;

export const AncillaryHandoffReasonSchema = z.enum([
  'SUBMIT_FAILED',
  'SUBMIT_UNCONFIRMED',
  'QUOTATION_REQUIRED',
  'ISSUE_REQUIRED',
  'ISSUE_FAILED',
  'FULFILLMENT_FAILED',
]);
export type AncillaryHandoffReason = z.infer<typeof AncillaryHandoffReasonSchema>;

export const AncillaryChildModalSchema = z.enum(['TRAVEL_SEAT', 'TRAVEL_LUGGAGE']);
export type AncillaryChildModal = z.infer<typeof AncillaryChildModalSchema>;

export const AncillaryLegacyEventSchema = z
  .object({
    /** Chave de idempotência estável: sha256(topic:childVoucherId:statusName). */
    eventKey: z.string().min(1),
    topic: AncillaryLegacyTopicSchema,
    child: z
      .object({
        voucherId: z.string().min(1),
        code: z.string().min(1),
        modal: AncillaryChildModalSchema,
        statusName: z.string().min(1),
        amount: z.number().nonnegative().nullable(),
        requestedAt: z.string().min(1),
      })
      .strict(),
    parentVoucherId: z.string().min(1),
    direction: AncillaryDirectionSchema.nullable(),
    reason: AncillaryHandoffReasonSchema.optional(),
    error: z
      .object({ source: z.string().min(1), message: z.string() })
      .strict()
      .nullable()
      .optional(),
    occurredAt: z.iso.datetime(),
  })
  .strict();
export type AncillaryLegacyEvent = z.infer<typeof AncillaryLegacyEventSchema>;

export const AncillaryEventIngestResultSchema = z
  .object({ matched: z.boolean(), sessionId: z.string().min(1).optional() })
  .strict();
export type AncillaryEventIngestResult = z.infer<typeof AncillaryEventIngestResultSchema>;

const OUTBOUND_FLIGHT_TYPES = new Set(['OUTWARD', 'FIRST']);
const RETURN_FLIGHT_TYPES = new Set(['BACK', 'SECOND']);

/**
 * `flightType` do legado → sentido. O app atual manda OUTWARD/BACK; as versões de
 * 2025 mandavam FIRST/SECOND (e THIRD…FIFTH em multi-trecho, sem sentido
 * ida/volta — devolve null e o casamento não acontece).
 */
export function ancillaryDirectionFromFlightType(
  flightType: string | null | undefined,
): AncillaryDirection | null {
  const normalized = (flightType ?? '').trim().toUpperCase();
  if (OUTBOUND_FLIGHT_TYPES.has(normalized)) return 'outbound';
  if (RETURN_FLIGHT_TYPES.has(normalized)) return 'return';
  return null;
}

/** Sentido → `flightType` que as mutations do legado recebem hoje. */
export function ancillaryFlightTypeFromDirection(
  direction: AncillaryDirection,
): 'OUTWARD' | 'BACK' {
  return direction === 'outbound' ? 'OUTWARD' : 'BACK';
}
